/**
 * Fixed Routes Context
 * 
 * Adapts CirculatorWebpage state management to work within Atlas.
 * Provides a bridge between Fixed Routes state and Atlas's AppContext.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import type { FixedRouteStop, FixedRoute, FixedRoutesState, RouteOperationAssignment, RouteGeometry, NetworkCostSettings } from '../types/fixedRoutes';
import { DEFAULT_COST_SETTINGS } from '../types/fixedRoutes';
import { DEFAULT_TRAFFIC_MULTIPLIER } from '../config/constants';
import type { DistanceUnit } from '../utils/fixedRoutes/distanceFormatter';
import { generateRouteColors } from '../utils/fixedRoutes/routeToLineConverter';
import { identifyAffectedSegments, updateRouteGeometryIncremental } from '../utils/fixedRoutes/incrementalRouteGeometryUpdater';
import { calculateTravelTimeAndDistance as osrmCalculateTravelTimeAndDistance } from '../services/routing/osrmClient';
import { calculateTravelTimeAndDistance as hereCalculateTravelTimeAndDistance } from '../services/routing/hereClient';
import { initializeWaypointsFromStops } from '../utils/fixedRoutes/createRouteFromKMLStops';

interface FixedRoutesContextType extends FixedRoutesState {
  addStop: (stop: FixedRouteStop) => void;
  updateStop: (stop: FixedRouteStop) => void;
  deleteStop: (stopId: string) => void;
  addRoute: (route: FixedRoute) => void;
  insertRoute: (route: FixedRoute, afterRouteId?: string) => void;
  updateRoute: (route: FixedRoute) => void;
  /**
   * Merge freshly-computed geometry into the LATEST route, preserving stops and all other fields.
   * Used by the map sync so persisting geometry from an async conversion can never clobber a stop
   * that was added while that conversion was in flight (replacing the whole route from a stale
   * snapshot used to silently delete such a stop — it rendered grey with no line and couldn't be
   * reconciled by dragging).
   */
  persistRouteGeometry: (routeId: string, geometry: RouteGeometry) => void;
  /**
   * Set the DRAFT operation/vehicle/day assignment buffer for a route (§7.1).
   * Local-only staging state; not pushed to Ops. Convenience over updateRoute().
   */
  setRouteOperationAssignments: (routeId: string, assignments: RouteOperationAssignment[]) => void;
  deleteRoute: (routeId: string) => void;
  deleteRoutesAndOrphanStops: (routeIds: string[]) => void;
  bulkAddStopsRoutes: (stops: FixedRouteStop[], routes: FixedRoute[], mergeMode: 'replace' | 'merge') => void;
  setTrafficMultiplier: (multiplier: number) => void;
  clearAllData: () => void;
  selectedRouteId: string | null;
  setSelectedRouteId: (routeId: string | null) => void;
  distanceUnit: DistanceUnit;
  setDistanceUnit: (unit: DistanceUnit) => void;
  /**
   * Network-default operating-cost rates (per-vehicle-per-day, per-km, hourly wage) + currency.
   * Set once for the network; individual routes may override via {@link FixedRoute.costOverride}.
   * Persisted to localStorage (v1). See the fixed-route cost-calculation feature.
   */
  costSettings: NetworkCostSettings;
  setCostSettings: (settings: NetworkCostSettings) => void;
  routeGenerationStatus: { isGenerating: boolean; routeName: string | null };
  setRouteGenerationStatus: (status: { isGenerating: boolean; routeName: string | null }) => void;
  editingRouteId: string | null;
  setEditingRouteId: (routeId: string | null) => void;
  enterEditMode: (routeId: string) => void;
  exitEditMode: () => void;
  importSimulationResultsNetwork: (stops: FixedRouteStop[], routes: FixedRoute[]) => void;
  /**
   * Route ids queued for Advance Rostering (shared with the Rules Engine page).
   * Set from the Fixed Routes drawer via "Use in Rostering".
   */
  rosteringRouteIds: string[];
  setRosteringRouteIds: (routeIds: string[]) => void;
  /** Bumped whenever routes are handed off so RosteringPanel can react even if ids are unchanged. */
  rosteringHandoffNonce: number;
}

const FixedRoutesContext = createContext<FixedRoutesContextType | undefined>(undefined);

/** localStorage key for the network-default cost settings (v1 persistence). */
const COST_SETTINGS_STORAGE_KEY = 'fr_cost_settings';

/** Load persisted cost settings, merged over the defaults so missing keys stay valid. */
function loadCostSettings(): NetworkCostSettings {
  try {
    const raw = typeof window !== 'undefined' ? window.localStorage.getItem(COST_SETTINGS_STORAGE_KEY) : null;
    if (!raw) return DEFAULT_COST_SETTINGS;
    const parsed = JSON.parse(raw) as Partial<NetworkCostSettings>;
    return {
      currency: typeof parsed.currency === 'string' && parsed.currency.trim() ? parsed.currency : DEFAULT_COST_SETTINGS.currency,
      dailyFixedCostPerVehicle: Number.isFinite(parsed.dailyFixedCostPerVehicle) ? Number(parsed.dailyFixedCostPerVehicle) : DEFAULT_COST_SETTINGS.dailyFixedCostPerVehicle,
      costPerKm: Number.isFinite(parsed.costPerKm) ? Number(parsed.costPerKm) : DEFAULT_COST_SETTINGS.costPerKm,
      hourlyWage: Number.isFinite(parsed.hourlyWage) ? Number(parsed.hourlyWage) : DEFAULT_COST_SETTINGS.hourlyWage,
    };
  } catch {
    return DEFAULT_COST_SETTINGS;
  }
}

export function pruneRoutesAndOrphanStops(
  currentRoutes: FixedRoute[],
  currentStops: FixedRouteStop[],
  routeIdsToDelete: string[]
): { routes: FixedRoute[]; stops: FixedRouteStop[] } {
  if (!Array.isArray(routeIdsToDelete) || routeIdsToDelete.length === 0) {
    return { routes: currentRoutes, stops: currentStops };
  }
  const routeIdSet = new Set(routeIdsToDelete);
  const remainingRoutes = currentRoutes.filter((route) => !routeIdSet.has(route.id));
  const remainingStopIds = new Set<string>();
  remainingRoutes.forEach((route) => {
    route.stops.forEach((s) => remainingStopIds.add(s.stopId));
  });
  const remainingStops = currentStops.filter((stop) => remainingStopIds.has(stop.id));
  return { routes: remainingRoutes, stops: remainingStops };
}

export function FixedRoutesProvider({ children }: { children: React.ReactNode }) {
  const [stops, setStops] = useState<FixedRouteStop[]>([]);
  const [routes, setRoutes] = useState<FixedRoute[]>([]);
  const [trafficMultiplier, setTrafficMultiplierState] = useState<number>(DEFAULT_TRAFFIC_MULTIPLIER);
  const [selectedRouteId, setSelectedRouteId] = useState<string | null>(null);
  const [distanceUnit, setDistanceUnitState] = useState<DistanceUnit>('metric');
  const [costSettings, setCostSettingsState] = useState<NetworkCostSettings>(loadCostSettings);
  const [routeGenerationStatus, setRouteGenerationStatus] = useState<{ isGenerating: boolean; routeName: string | null }>({ isGenerating: false, routeName: null });
  const [editingRouteId, setEditingRouteIdState] = useState<string | null>(null);
  const [rosteringRouteIds, setRosteringRouteIdsState] = useState<string[]>([]);
  const [rosteringHandoffNonce, setRosteringHandoffNonce] = useState(0);
  const nextColorIndexRef = useRef<number>(0);

  // Routes and stops are now session-only (no localStorage persistence)
  
  // Automatically assign colors to routes that don't have them
  useEffect(() => {
    // Check all routes and find ones without colors
    const routesWithoutColors = routes.filter(r => !r.color);
    
    if (routesWithoutColors.length > 0) {
      console.log(`🎨 Assigning colors to ${routesWithoutColors.length} routes without colors`);
      // Generate a large palette to ensure we have enough colors
      const colorPalette = generateRouteColors(Math.max(routesWithoutColors.length + nextColorIndexRef.current, 20));
      
      setRoutes(prevRoutes => {
        // Check if we actually need to update anything
        const needsUpdate = prevRoutes.some(r => !r.color);
        if (!needsUpdate) {
          return prevRoutes;
        }
        
        const updatedRoutes = prevRoutes.map(route => {
          if (!route.color) {
            const index = nextColorIndexRef.current;
            const assignedColor = colorPalette[index % colorPalette.length];
            console.log(`  ✓ Route "${route.name}" (${route.id}) assigned color[${index}]: ${assignedColor}`);
            nextColorIndexRef.current++;
            return { ...route, color: assignedColor };
          }
          return route;
        });
        return updatedRoutes;
      });
    }
  }, [routes.length, routes.filter(r => !r.color).map(r => r.id).join(',')]); // Run when route count changes or when routes without colors change
  
  // Store old geometry before edits to identify affected segments
  const routeGeometryCache = useRef<Map<string, [number, number][]>>(new Map());

  // Latest routes/stops snapshots for the geometry-edit handler. It subscribes once
  // and runs async work across awaits, so reading these refs (instead of the values
  // captured in the effect closure) prevents acting on a stale snapshot when a
  // recalculation races with a concurrent route/stop update.
  const routesRef = useRef(routes);
  const stopsRef = useRef(stops);
  useEffect(() => { routesRef.current = routes; }, [routes]);
  useEffect(() => { stopsRef.current = stops; }, [stops]);

  /**
   * Initialize waypoints for a route if missing
   * Called when routes are added or loaded to ensure they have waypoints
   */
  const ensureRouteHasWaypoints = useCallback((route: FixedRoute, allStops: FixedRouteStop[]): FixedRoute => {
    if (route.waypoints && route.waypoints.length > 0) {
      return route; // Already has waypoints
    }

    // Routes that already carry a precomputed path (GTFS-imported shapes,
    // simulation-results imports) must NOT get stop-derived waypoints. Waypoints
    // feed into the geometry-cache hash (computeRouteGeometryHash), so adding them
    // here — after the importer stamped geometry.stopsHash with none — invalidates
    // the cached shape and forces a full OSRM re-route of every route on import
    // (slow, and it replaces the real published route geometry with a driving
    // path). Such routes don't derive their geometry from stops+waypoints, so they
    // don't need them; the edit flow initializes waypoints later if the user edits.
    if (route.geometry?.coordinates && route.geometry.coordinates.length > 1) {
      return route;
    }

    // Initialize waypoints from stops
    const waypoints = initializeWaypointsFromStops(
      route.stops
        .map(stopConfig => allStops.find(s => s.id === stopConfig.stopId))
        .filter((stop): stop is FixedRouteStop => stop !== undefined && stop.coordinates !== undefined)
    );
    
    if (waypoints.length > 0) {
      return { ...route, waypoints };
    }
    
    return route; // No stops available, return as-is
  }, []);

  // Listen for custom events to create routes from KML (for development/testing)
  useEffect(() => {
    const handleCreateRouteFromKML = (event: CustomEvent<{ stops: FixedRouteStop[]; route: FixedRoute }>) => {
      const { stops: newStops, route: newRoute } = event.detail;
      // Add stops (check for duplicates)
      newStops.forEach(stop => {
        setStops(prev => {
          const exists = prev.find(s => s.id === stop.id);
          if (!exists) {
            return [...prev, stop];
          }
          return prev;
        });
      });
      // Add route (check for duplicates)
      setRoutes(prev => {
        const exists = prev.find(r => r.id === newRoute.id);
        if (!exists) {
          return [...prev, newRoute];
        }
        return prev;
      });
    };
    
    window.addEventListener('createRouteFromKML', handleCreateRouteFromKML as EventListener);
    return () => {
      window.removeEventListener('createRouteFromKML', handleCreateRouteFromKML as EventListener);
    };
  }, []);

  // Listen for route geometry edits and trigger road snapping recalculation
  useEffect(() => {
    const recalculateSegmentMetrics = async (
      routeForMetrics: FixedRoute,
      affectedSegmentIndices: number[]
    ): Promise<FixedRoute['segments']> => {
      const routeStops = routeForMetrics.stops
        .map((stopConfig) => stopsRef.current.find((s) => s.id === stopConfig.stopId))
        .filter((stop): stop is FixedRouteStop => stop !== undefined && stop.coordinates !== undefined);

      const updatedSegments = [...routeForMetrics.segments];
      const validIndices = Array.from(
        new Set(
          affectedSegmentIndices.filter((idx) => idx >= 0 && idx < routeStops.length - 1)
        )
      );

      await Promise.allSettled(
        validIndices.map(async (segIdx) => {
          const fromStop = routeStops[segIdx];
          const toStop = routeStops[segIdx + 1];
          const result = routeForMetrics.isBusRoute
            ? await hereCalculateTravelTimeAndDistance(fromStop, toStop)
            : await osrmCalculateTravelTimeAndDistance(fromStop, toStop);

          const targetIdx = updatedSegments.findIndex(
            (s) => s.fromStopId === fromStop.id && s.toStopId === toStop.id
          );
          if (targetIdx !== -1) {
            updatedSegments[targetIdx] = {
              ...updatedSegments[targetIdx],
              travelTimeMinutes: result.travelTimeMinutes,
              distanceMeters: result.distanceMeters,
            };
          }
        })
      );

      return updatedSegments;
    };

    const handleRouteGeometryEdited = async (event: Event) => {
      const customEvent = event as CustomEvent<{
        routeId: string;
        coordinates: [number, number][];
        oldCoordinates?: [number, number][];
        affectedSegmentIndex?: number;
        skipGeometryRecalc?: boolean;
      }>;
      const { routeId, coordinates: newCoordinates, oldCoordinates, affectedSegmentIndex, skipGeometryRecalc } = customEvent.detail;
      
      console.log(`[FixedRoutesContext] Received fixedRouteGeometryEdited event for route ${routeId}${affectedSegmentIndex !== undefined ? `, segment ${affectedSegmentIndex}` : ''}`);
      
      const route = routesRef.current.find(r => r.id === routeId);
      if (!route) {
        console.warn(`[FixedRoutesContext] Route ${routeId} not found`);
        return;
      }
      
      // Ensure route is marked as manually edited (might not be set yet if event dispatched before route update)
      if (!route.isManuallyEdited) {
        console.log(`[FixedRoutesContext] Marking route ${routeId} as manually edited`);
        setRoutes(prev => prev.map(r => r.id === routeId ? { ...r, isManuallyEdited: true } : r));
      }

      // Use oldCoordinates from event if provided, otherwise fall back to cache
      const oldGeometry = oldCoordinates || routeGeometryCache.current.get(routeId) || route.geometry?.coordinates || [];
      
      // Update cache with new geometry
      routeGeometryCache.current.set(routeId, newCoordinates);

      // Identify affected segments
      // If a specific segment index is provided (from vertex drag), use only that segment
      // Otherwise, use the identifyAffectedSegments function to find all affected segments
      let affectedSegmentIndices: number[];
      if (affectedSegmentIndex !== undefined && affectedSegmentIndex >= 0) {
        // Recalculate the specific segment that contains the dragged vertex, plus its immediate neighbor.
        // In practice, a dragged vertex can affect the adjacent segment boundary as well.
        const indices = new Set<number>([affectedSegmentIndex, affectedSegmentIndex - 1]);
        affectedSegmentIndices = Array.from(indices).filter((i) => i >= 0);
        console.log(
          `[FixedRoutesContext] Recalculating segment(s) ${affectedSegmentIndices.join(', ')} for route ${routeId} (vertex drag)`
        );
      } else {
        // Use the general identification function
        affectedSegmentIndices = identifyAffectedSegments(
          route,
          oldGeometry as [number, number][],
          newCoordinates,
          stopsRef.current
        );
      }

      if (affectedSegmentIndices.length === 0) {
        return; // No segments affected
      }

      console.log(`[FixedRoutesContext] Recalculating ${affectedSegmentIndices.length} affected segments for route ${routeId}`);

      if (skipGeometryRecalc) {
        try {
          const updatedSegments = await recalculateSegmentMetrics(route, affectedSegmentIndices);
          setRoutes((prev) =>
            prev.map((r) =>
              r.id === routeId
                ? {
                    ...r,
                    segments: updatedSegments,
                    isManuallyEdited: true,
                  }
                : r
            )
          );
        } catch (error) {
          console.error(`[FixedRoutesContext] Error recalculating segment metrics for route ${routeId}:`, error);
        }
        return;
      }

      try {
        // Recalculate geometry for affected segments
        const updatedRoute = await updateRouteGeometryIncremental(
          route,
          stopsRef.current,
          affectedSegmentIndices
        );

        if (!updatedRoute) {
          console.warn(`[FixedRoutesContext] Failed to recalculate geometry for route ${routeId}`);
          return;
        }

        const updatedSegments = await recalculateSegmentMetrics(updatedRoute, affectedSegmentIndices);

        // Update route with new geometry and segment metrics
        const finalRoute: FixedRoute = {
          ...updatedRoute,
          segments: updatedSegments,
          isManuallyEdited: true, // Keep manually edited flag
        };

        setRoutes(prev => prev.map(r => r.id === routeId ? finalRoute : r));
        
        console.log(`[FixedRoutesContext] Successfully recalculated route ${routeId}`);
      } catch (error) {
        console.error(`[FixedRoutesContext] Error recalculating route ${routeId}:`, error);
        // Keep manual edits even if recalculation fails
      }
    };

    window.addEventListener('fixedRouteGeometryEdited', handleRouteGeometryEdited);
    return () => {
      window.removeEventListener('fixedRouteGeometryEdited', handleRouteGeometryEdited);
    };
    // Subscribe once: the handler reads the latest routes/stops via refs, so it no
    // longer needs to re-subscribe (and close over a fresh snapshot) on every change.
  }, []);

  // Cache geometry when routes are updated (to track changes)
  useEffect(() => {
    routes.forEach(route => {
      if (route.geometry?.coordinates) {
        routeGeometryCache.current.set(route.id, route.geometry.coordinates as [number, number][]);
      }
    });
  }, [routes]);

  const addStop = useCallback((stop: FixedRouteStop) => {
    // Validate stop has valid coordinates before adding
    const coords = stop.coordinates;
    const lat = coords?.lat;
    const lng = coords?.lng;
    
    if (!coords || 
        lat === undefined || 
        lng === undefined ||
        lat === null || 
        lng === null ||
        typeof lat !== 'number' || 
        typeof lng !== 'number' ||
        isNaN(lat) || 
        isNaN(lng) ||
        !isFinite(lat) ||
        !isFinite(lng)) {
      console.error(`[FixedRoutesContext] Cannot add stop ${stop.id} - invalid coordinates:`, {
        stop,
        coords,
        lat,
        lng
      });
      return; // Don't add stop with invalid coordinates
    }
    
    setStops((prev) => [...prev, stop]);
  }, []);

  const updateStop = useCallback((updatedStop: FixedRouteStop) => {
    // Validate stop has valid coordinates before updating
    const coords = updatedStop.coordinates;
    const lat = coords?.lat;
    const lng = coords?.lng;
    
    if (!coords || 
        lat === undefined || 
        lng === undefined ||
        lat === null || 
        lng === null ||
        typeof lat !== 'number' || 
        typeof lng !== 'number' ||
        isNaN(lat) || 
        isNaN(lng) ||
        !isFinite(lat) ||
        !isFinite(lng)) {
      console.error(`[FixedRoutesContext] Cannot update stop ${updatedStop.id} - invalid coordinates:`, {
        stop: updatedStop,
        coords,
        lat,
        lng
      });
      return; // Don't update stop with invalid coordinates
    }
    
    setStops((prev) => prev.map((s) => (s.id === updatedStop.id ? updatedStop : s)));
  }, []);

  const deleteStop = useCallback((stopId: string) => {
    setStops((prev) => prev.filter((s) => s.id !== stopId));
    // Also remove from any routes that use this stop
    setRoutes((prev) =>
      prev
        .map((route) => ({
          ...route,
          stops: route.stops.filter((s) => s.stopId !== stopId),
          segments: route.segments.filter(
            (seg) => seg.fromStopId !== stopId && seg.toStopId !== stopId
          ),
        }))
        .filter((route) => route.stops.length >= 2) // Remove routes with less than 2 stops
    );
  }, []);

  const addRoute = useCallback((route: FixedRoute) => {
    setRoutes((prev) => {
      // Ensure route has waypoints initialized
      let routeToAdd = ensureRouteHasWaypoints(route, stops);
      
      // If route doesn't have a color, assign one immediately using global index
      if (!routeToAdd.color) {
        // Generate a large palette to ensure we have enough colors
        const colorPalette = generateRouteColors(Math.max(prev.length + 1, 20));
        const index = nextColorIndexRef.current;
        routeToAdd = { ...routeToAdd, color: colorPalette[index % colorPalette.length] };
        nextColorIndexRef.current++;
        console.log(`🎨 Assigned color[${index}] to new route "${routeToAdd.name}": ${routeToAdd.color}`);
      }
      return [...prev, routeToAdd];
    });
  }, [stops, ensureRouteHasWaypoints]);

  const insertRoute = useCallback((route: FixedRoute, afterRouteId?: string) => {
    if (!afterRouteId) {
      // If no afterRouteId specified, just add to the end
      addRoute(route);
      return;
    }
    
    setRoutes((prev) => {
      const index = prev.findIndex((r) => r.id === afterRouteId);
      if (index === -1) {
        // Route not found, add to end
        return [...prev, route];
      }
      // Insert right after the specified route
      const newRoutes = [...prev];
      newRoutes.splice(index + 1, 0, route);
      return newRoutes;
    });
  }, [addRoute]);

  const updateRoute = useCallback((updatedRoute: FixedRoute) => {
    setRoutes((prev) => {
      const updated = prev.map((r) => (r.id === updatedRoute.id ? updatedRoute : r));
      // If updated route doesn't have a color, assign one immediately
      const routeWithoutColor = updated.find(r => r.id === updatedRoute.id && !r.color);
      if (routeWithoutColor) {
        const routesWithoutColors = updated.filter(r => !r.color);
        const colorPalette = generateRouteColors(Math.max(routesWithoutColors.length, 1));
        const index = routesWithoutColors.findIndex(r => r.id === updatedRoute.id);
        if (index >= 0) {
          routeWithoutColor.color = colorPalette[index % colorPalette.length];
          console.log(`🎨 Assigned color to updated route "${routeWithoutColor.name}": ${routeWithoutColor.color}`);
        }
      }
      return updated;
    });
  }, []);

  const persistRouteGeometry = useCallback((routeId: string, geometry: RouteGeometry) => {
    // Geometry-only merge onto the CURRENT route. Critically this does NOT replace the route from a
    // snapshot: if a stop was added while geometry was being computed asynchronously, that stop stays
    // on the route here, and the next sync recomputes geometry to include it. (Replacing the whole
    // route from a stale snapshot silently dropped such a stop — it rendered grey with no line.)
    setRoutes((prev) => prev.map((r) => (r.id === routeId ? { ...r, geometry } : r)));
  }, []);

  const setRouteOperationAssignments = useCallback(
    (routeId: string, assignments: RouteOperationAssignment[]) => {
      setRoutes((prev) =>
        prev.map((r) => (r.id === routeId ? { ...r, operationAssignments: assignments } : r)),
      );
    },
    [],
  );

  const deleteRoute = useCallback((routeId: string) => {
    setRoutes((prev) => prev.filter((r) => r.id !== routeId));
  }, []);

  const deleteRoutesAndOrphanStops = useCallback((routeIds: string[]) => {
    if (!Array.isArray(routeIds) || routeIds.length === 0) return;
    const routeIdSet = new Set(routeIds);

    setRoutes((prevRoutes) => {
      setStops((prevStops) => pruneRoutesAndOrphanStops(prevRoutes, prevStops, routeIds).stops);
      const remainingRoutes = prevRoutes.filter((route) => !routeIdSet.has(route.id));

      setSelectedRouteId((prevSelected) =>
        prevSelected && routeIdSet.has(prevSelected) ? null : prevSelected
      );
      setEditingRouteIdState((prevEditing) =>
        prevEditing && routeIdSet.has(prevEditing) ? null : prevEditing
      );

      return remainingRoutes;
    });
  }, []);

  // Helper function to validate stop coordinates
  const isValidStop = (stop: FixedRouteStop): boolean => {
    const coords = stop.coordinates;
    const lat = coords?.lat;
    const lng = coords?.lng;
    
    return coords && 
           lat !== undefined && 
           lng !== undefined &&
           lat !== null && 
           lng !== null &&
           typeof lat === 'number' && 
           typeof lng === 'number' &&
           !isNaN(lat) && 
           !isNaN(lng) &&
           isFinite(lat) &&
           isFinite(lng);
  };

  const bulkAddStopsRoutes = useCallback((newStops: FixedRouteStop[], newRoutes: FixedRoute[], mergeMode: 'replace' | 'merge') => {
    // Filter out stops with invalid coordinates before processing
    const validStops = newStops.filter(stop => {
      if (!isValidStop(stop)) {
        console.warn(`[FixedRoutesContext] Filtering out stop ${stop.id} with invalid coordinates:`, stop.coordinates);
        return false;
      }
      return true;
    });
    
    if (validStops.length !== newStops.length) {
      console.warn(`[FixedRoutesContext] Filtered out ${newStops.length - validStops.length} stops with invalid coordinates`);
    }
    
    // Performance: Chunk large imports to avoid blocking the UI thread
    const CHUNK_SIZE = 1000;
    const isLargeImport = validStops.length > 5000 || newRoutes.length > 1000;

    if (mergeMode === 'replace') {
      if (isLargeImport) {
        // Chunk stops for large imports
        setStops([]); // Clear first
        let stopIndex = 0;
        const addStopsChunk = () => {
          const chunk = validStops.slice(stopIndex, stopIndex + CHUNK_SIZE);
          setStops((prev) => [...prev, ...chunk]);
          stopIndex += CHUNK_SIZE;
          if (stopIndex < validStops.length) {
            // Use requestIdleCallback or setTimeout to yield to UI
            setTimeout(addStopsChunk, 0);
          } else {
            // All stops added, now add routes
            setRoutes([]);
            let routeIndex = 0;
            const addRoutesChunk = () => {
              const chunk = newRoutes.slice(routeIndex, routeIndex + CHUNK_SIZE);
              setRoutes((prev) => [...prev, ...chunk]);
              routeIndex += CHUNK_SIZE;
              if (routeIndex < newRoutes.length) {
                setTimeout(addRoutesChunk, 0);
              }
            };
            addRoutesChunk();
          }
        };
        addStopsChunk();
      } else {
        // Small import: do it all at once
        setStops(validStops);
        // Initialize waypoints for routes that don't have them
        const routesWithWaypoints = newRoutes.map(route => ensureRouteHasWaypoints(route, validStops));
        setRoutes(routesWithWaypoints);
      }
      return;
    }

    // Merge mode: handle duplicates with chunking for large imports
    if (isLargeImport) {
      // Chunk stops
      let stopIndex = 0;
      const mergeStopsChunk = () => {
        const chunk = validStops.slice(stopIndex, stopIndex + CHUNK_SIZE);
        setStops((prevStops) => {
          // Also filter out any existing stops with invalid coordinates
          const validPrevStops = prevStops.filter(isValidStop);
          const stopMap = new Map(validPrevStops.map(s => [s.id, s]));
          chunk.forEach(stop => {
            stopMap.set(stop.id, stop);
          });
          return Array.from(stopMap.values());
        });
        stopIndex += CHUNK_SIZE;
        if (stopIndex < validStops.length) {
          setTimeout(mergeStopsChunk, 0);
        } else {
          // All stops merged, now merge routes
          let routeIndex = 0;
          const mergeRoutesChunk = () => {
            const chunk = newRoutes.slice(routeIndex, routeIndex + CHUNK_SIZE);
            setRoutes((prevRoutes) => {
              const routeMap = new Map(prevRoutes.map(r => [r.id, r]));
              chunk.forEach(route => {
                let routeId = route.id;
                let suffix = 1;
                while (routeMap.has(routeId)) {
                  routeId = `${route.id} (GTFS${suffix > 1 ? ` ${suffix}` : ''})`;
                  suffix++;
                }
                // Ensure route has waypoints initialized
                const routeWithWaypoints = ensureRouteHasWaypoints({ ...route, id: routeId }, validStops);
                routeMap.set(routeId, routeWithWaypoints);
              });
              return Array.from(routeMap.values());
            });
            routeIndex += CHUNK_SIZE;
            if (routeIndex < newRoutes.length) {
              setTimeout(mergeRoutesChunk, 0);
            }
          };
          mergeRoutesChunk();
        }
      };
      mergeStopsChunk();
    } else {
      // Small import: merge all at once
      setStops((prevStops) => {
        // Also filter out any existing stops with invalid coordinates
        const validPrevStops = prevStops.filter(isValidStop);
        const stopMap = new Map(validPrevStops.map(s => [s.id, s]));
        validStops.forEach(stop => {
          stopMap.set(stop.id, stop);
        });
        return Array.from(stopMap.values());
      });

      setRoutes((prevRoutes) => {
        const routeMap = new Map(prevRoutes.map(r => [r.id, r]));
        newRoutes.forEach(route => {
          let routeId = route.id;
          let suffix = 1;
          while (routeMap.has(routeId)) {
            routeId = `${route.id} (GTFS${suffix > 1 ? ` ${suffix}` : ''})`;
            suffix++;
          }
          // Ensure route has waypoints initialized
          const routeWithWaypoints = ensureRouteHasWaypoints({ ...route, id: routeId }, validStops);
          routeMap.set(routeId, routeWithWaypoints);
        });
        return Array.from(routeMap.values());
      });
    }
  }, [ensureRouteHasWaypoints]);

  const setTrafficMultiplier = useCallback((multiplier: number) => {
    if (multiplier > 0) {
      setTrafficMultiplierState(multiplier);
    }
  }, []);

  const setDistanceUnit = useCallback((unit: DistanceUnit) => {
    setDistanceUnitState(unit);
  }, []);

  const setCostSettings = useCallback((settings: NetworkCostSettings) => {
    setCostSettingsState(settings);
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(COST_SETTINGS_STORAGE_KEY, JSON.stringify(settings));
      }
    } catch {
      // Persistence is best-effort; ignore quota/availability errors.
    }
  }, []);

  const setEditingRouteId = useCallback((routeId: string | null) => {
    setEditingRouteIdState(routeId);
  }, []);

  const enterEditMode = useCallback((routeId: string) => {
    // Ensure only one route can be in edit mode at a time
    // If another route is already being edited, exit it first
    if (editingRouteId && editingRouteId !== routeId) {
      setEditingRouteIdState(null);
    }
    
    setEditingRouteIdState(routeId);
    
    // Mark route as manually edited
    setRoutes((prev) =>
      prev.map((r) =>
        r.id === routeId ? { ...r, isManuallyEdited: true } : r
      )
    );
  }, [editingRouteId]);

  const exitEditMode = useCallback(() => {
    setEditingRouteIdState(null);
  }, []);

  const clearAllData = useCallback(() => {
    setStops([]);
    setRoutes([]);
    setTrafficMultiplierState(DEFAULT_TRAFFIC_MULTIPLIER);
    setSelectedRouteId(null);
    setEditingRouteIdState(null);
  }, []);

  const importSimulationResultsNetwork = useCallback((newStops: FixedRouteStop[], newRoutes: FixedRoute[]) => {
    const preservedRoutes = routes.filter((r) => r.simulationImport?.source !== 'simulation_results');
    const preservedRouteStopIds = new Set<string>();
    preservedRoutes.forEach((route) => {
      route.stops.forEach((s) => preservedRouteStopIds.add(s.stopId));
    });

    const preservedStops = stops.filter(
      (s) => s.simulationImport?.runId == null || preservedRouteStopIds.has(s.id)
    );

    const validNewStops = newStops.filter(isValidStop);
    const validNewRoutes = newRoutes.map((route) => ensureRouteHasWaypoints(route, validNewStops));

    const stopMap = new Map<string, FixedRouteStop>();
    preservedStops.forEach((s) => stopMap.set(s.id, s));
    validNewStops.forEach((s) => stopMap.set(s.id, s));

    setStops(Array.from(stopMap.values()));
    setRoutes([...preservedRoutes, ...validNewRoutes]);
    setSelectedRouteId(validNewRoutes[0]?.id ?? preservedRoutes[0]?.id ?? null);
  }, [routes, stops, ensureRouteHasWaypoints]);

  const setRosteringRouteIds = useCallback((routeIds: string[]) => {
    setRosteringRouteIdsState(Array.from(new Set(routeIds.filter(Boolean))));
    setRosteringHandoffNonce((n) => n + 1);
  }, []);

  // Memoize the context value so a state change (e.g. each captured stop's optimistic add,
  // segment-timing result, or reverse-geocode name update) doesn't give `value` a new identity
  // and re-render ALL consumers — including the map's line/marker renderers and the 4700-line
  // RouteBuilder. Without this, rapid stop capture saturates the renderer. All callbacks below
  // are useCallback-stable and the setters are stable, so this only recomputes on real data change.
  const value: FixedRoutesContextType = useMemo(() => ({
    stops,
    routes,
    trafficMultiplier,
    addStop,
    updateStop,
    deleteStop,
    addRoute,
    insertRoute,
    updateRoute,
    persistRouteGeometry,
    setRouteOperationAssignments,
    deleteRoute,
    deleteRoutesAndOrphanStops,
    bulkAddStopsRoutes,
    setTrafficMultiplier,
    clearAllData,
    selectedRouteId,
    setSelectedRouteId,
    distanceUnit,
    setDistanceUnit,
    costSettings,
    setCostSettings,
    routeGenerationStatus,
    setRouteGenerationStatus,
    editingRouteId,
    setEditingRouteId,
    enterEditMode,
    exitEditMode,
    importSimulationResultsNetwork,
    rosteringRouteIds,
    setRosteringRouteIds,
    rosteringHandoffNonce,
  }), [
    stops, routes, trafficMultiplier, selectedRouteId, distanceUnit, costSettings, routeGenerationStatus, editingRouteId,
    rosteringRouteIds, rosteringHandoffNonce,
    addStop, updateStop, deleteStop, addRoute, insertRoute, updateRoute, persistRouteGeometry, setRouteOperationAssignments,
    deleteRoute, deleteRoutesAndOrphanStops, bulkAddStopsRoutes, setTrafficMultiplier, clearAllData,
    setSelectedRouteId, setDistanceUnit, setCostSettings, setRouteGenerationStatus, setEditingRouteId, enterEditMode,
    exitEditMode, importSimulationResultsNetwork, setRosteringRouteIds,
  ]);

  return <FixedRoutesContext.Provider value={value}>{children}</FixedRoutesContext.Provider>;
}

export function useFixedRoutes() {
  const context = useContext(FixedRoutesContext);
  if (context === undefined) {
    throw new Error('useFixedRoutes must be used within a FixedRoutesProvider');
  }
  return context;
}

